import * as Actions from 'store/authentication/actions';
import { createReducer } from '@reduxjs/toolkit';
import { createFetchReducer } from 'store/setLocalStore';
import { AUTH_LITE_LOGIN, AUTH_VERIFY_TOKEN } from 'store/authentication/actions.common';
import { TpsStatus } from './tpsHelpers';
import { AuthLiteReducer, AuthorizeResult, LoginType, RefreshResult } from './types';

export type AuthResult = AuthorizeResult | RefreshResult | undefined | null;

export interface AuthResults {
  dfdAuth?: AuthResult | null;
  isFetching?: boolean;
  authOpen?: boolean;
  error?: Error | string | null;
}

export interface AuthenticationState extends AuthResults {
  isAuthenticating: boolean;
  authOpen?: boolean;
  biometricsOpen?: boolean;
  blockedRoute: string;
  error: null | Error;
  tpsAuthStatus: TpsStatus[];
  tpsAuthStatusFetching?: boolean;
  tpsAuthStatusError?: Error | null;
  protectedRouteModalIsOpen: boolean;
  hideGuestBenefitsModal: boolean;
  loginType?: LoginType;
}

export const authenticationInitialState: AuthenticationState = {
  dfdAuth: null,
  tpsAuthStatus: [],
  isAuthenticating: false,
  blockedRoute: '',
  error: null,
  protectedRouteModalIsOpen: false,
  hideGuestBenefitsModal: false
};

const authenticationReducer = createReducer(authenticationInitialState, {
  [Actions.AUTHENTICATE_USER]: state => {
    state.isAuthenticating = true;
    state.error = null;
  },
  [Actions.AUTHENTICATE_USER_SUCCESS]: (state, action) => {
    return { ...state, ...action.payload, isAuthenticating: false };
  },
  [Actions.AUTHENTICATE_USER_FAIL]: (state, action) => {
    state.isAuthenticating = false;
    state.dfdAuth = null;
    state.tpsAuthStatus = [];
    state.blockedRoute = '';
    state.error = action.error;
  },
  [Actions.REFRESH_DFD_TOKEN_SUCCESS]: (state, action) => {
    state.dfdAuth = action.payload;
    state.error = null;
    state.isAuthenticating = false;
  },
  [Actions.AUTH_OPEN]: (state, action) => {
    state.authOpen = action.payload || false;
  },
  [Actions.BIOMETRICS_OPEN]: (state, action) => {
    state.biometricsOpen = action.payload || false;
  },
  [Actions.SET_TPS_AUTH_STATUS]: (state, action) => {
    state.tpsAuthStatus = action.payload;
  },
  [Actions.SET_TPS_AUTH_STATUS_FETCHING]: (state, action) => {
    state.tpsAuthStatusFetching = action.payload;
  },
  [Actions.SET_TPS_AUTH_STATUS_ERROR]: (state, action) => {
    state.tpsAuthStatusError = action.payload;
  },
  [Actions.SET_LOGIN_TYPE]: (state, action) => {
    state.loginType = action.payload;
  },
  [Actions.AUTHENTICATE_SET_BLOCK_ROUTE]: (state, action) => {
    state.blockedRoute = action.payload;
  },
  [Actions.TOGGLE_PROTECTED_ROUTE_MODAL]: state => {
    state.protectedRouteModalIsOpen = !state.protectedRouteModalIsOpen;
  },
  [Actions.HIDE_GUEST_BENEFITS_MODAL]: state => {
    state.hideGuestBenefitsModal = true;
  },
  [Actions.AUTHENTICATE_RESET_ACCESS_TOKEN]: () => authenticationInitialState,
  [Actions.AUTHENTICATE_LOGOUT]: () => authenticationInitialState,

  [Actions.FETCH_TPS_TOKEN_SUCCESS]: (state, action) => {
    const status = state.tpsAuthStatus?.find(s => s.id === action.payload.source);

    if (status) {
      status.accessToken = action.payload.data.accessToken;
      status.expirationDate = action.payload.data.expirationDate;
    }
  }
});

export default authenticationReducer;

export const authLite: AuthLiteReducer = createFetchReducer(AUTH_LITE_LOGIN);
export const tokenVerify = createFetchReducer<{ active: boolean }>(AUTH_VERIFY_TOKEN);
